<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template #main-filters>
        <filters
          :activity-type-filter.sync="activityTypeFilter"
          :sector-ids-filter.sync="sectorIdsFilter"
          :region-ids-filter.sync="regionIdsFilter"
          :rems-filter.sync="remsFilter"
          :unit-name-filter.sync="unitNameFilter"
          :facility-category-filter.sync="facilityCategoryFilter"
          :fecha-inicio-filter.sync="fechaInicioFilter"
          :fecha-fin-filter.sync="fechaFinFilter"
          :status-filter.sync="statusFilter"
        />
      </template>
    </filter-modal>

    <b-card class="m-2" no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="text-capitalize">{{ $t('table.show') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t('table.entries') }}</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search') + '...'"
              />
              <b-button
                v-if="$can('create', 'Planning')"
                variant="primary"
                class="text-capitalize ml-1"
                @click="goToPlanningCreate"
              >
                {{ $t('planning.start_programming') }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span>{{ $tc(data.label, 1) }}</span>
        </template>
        <template #head(actions)="data">
          <span>{{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            v-if="data.item.planning_id_for_link && $can('update', 'Planning')"
            variant="link"
            no-caret
            boundary="viewport"
            :right="$store.state.appConfig.isRTL"
            class="planning-actions-dropdown"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item @click="goToPlanningDetail(data.item.planning_id_for_link)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('actions.edit') }}</span>
            </b-dropdown-item>
          </b-dropdown>
          <span v-else class="text-muted">-</span>
        </template>
        <template #cell(planning_id_display)="data">
          {{ data.item.planning_id_display || '-' }}
        </template>
        <template #cell(activity_type_display)="data">
          {{
            data.item.activity_type_translate_key
              ? $t(data.item.activity_type_translate_key)
              : (data.item.activity_type_display || '-')
          }}
        </template>
        <template #cell(sector_name)="data">
          {{ data.item.sector_name || '-' }}
        </template>
        <template #cell(region_name)="data">
          {{ data.item.region_name || '-' }}
        </template>
        <template #cell(rems_display)="data">
          {{ data.item.rems_display || '-' }}
        </template>
        <template #cell(unit_name)="data">
          {{ data.item.unit_name || '-' }}
        </template>
        <template #cell(facility_category_name)="data">
          {{ data.item.facility_category_name || '-' }}
        </template>
        <template #cell(start_date_formatted)="data">
          {{ data.item.start_date_formatted || '-' }}
        </template>
        <template #cell(target_date_formatted)="data">
          {{ data.item.target_date_formatted || '-' }}
        </template>
        <template #cell(planning_status)="data">
          <div>
            <div>{{ resolveStatusLabel(data.item.planning_status) }}</div>
            <small
              v-if="data.item.last_status && data.item.last_status.changed_by"
              class="text-muted d-block"
            >
              {{
                $t('planning.list_status_by', {
                  name: getActorPrimary(data.item.last_status.changed_by),
                })
              }}
            </small>
          </div>
        </template>
        <template #cell(assigned_to)="data">
          <div>
            <div>{{ getActorPrimary(data.item.assigned_to) }}</div>
            <small class="text-muted">{{ getActorSecondary(data.item.assigned_to) }}</small>
          </div>
        </template>
        <template #cell(in_review_by)="data">
          <div>
            <div>{{ getActorPrimary(data.item.in_review_by) }}</div>
            <small class="text-muted">{{ getActorSecondary(data.item.in_review_by) }}</small>
          </div>
        </template>
        <template #cell(accepted_or_cancelled_by)="data">
          <div>
            <div>{{ getActorPrimary(data.item.accepted_or_cancelled_by) }}</div>
            <small class="text-muted">{{ getActorSecondary(data.item.accepted_or_cancelled_by) }}</small>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted text-capitalize">
              {{ $t('table.showing', { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }}
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <Pagination
              :total-list="totalList"
              :current-page="currentPage"
              :per-page="perPage"
              @updatePagination="updatePagination"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import vSelect from 'vue-select'
import { ref, onMounted } from '@vue/composition-api'
import useList from './useList'
import BreadcrumbMenu from '@/components/BreadcrumbMenu.vue'
import FilterModal from '@/components/Filters/FilterModal.vue'
import ButtonFilters from '@/components/Filters/ButtonFilters.vue'
import Pagination from '@/components/Pagination.vue'
import Filters from './Filters.vue'
import store from '@/store'
import router from '@/router'

export default {
  components: {
    BreadcrumbMenu,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    FeatherIcon,
    vSelect,
    FilterModal,
    ButtonFilters,
  },
  setup(props, { root }) {
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDate,
      activityTypeFilter,
      sectorIdsFilter,
      regionIdsFilter,
      remsFilter,
      unitNameFilter,
      facilityCategoryFilter,
      fechaInicioFilter,
      fechaFinFilter,
      statusFilter,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      getPlanningStatusTitle,
      getActorPrimary,
      getActorSecondary,
    } = useList()

    const resolveStatusLabel = (status) =>
      getPlanningStatusTitle(status, (key) => root.$t(key))

    const goToPlanningCreate = () => {
      router.push({ name: 'planningCreate' })
    }

    const goToPlanningDetail = (id) => {
      if (id == null) return
      router.push({ name: 'planningEdit', params: { id: String(id) } })
    }

    onMounted(() => {})

    const updatePagination = (page) => {
      const nextPage = Number(page)
      if (Number.isNaN(nextPage) || nextPage < 1) return
      currentPage.value = nextPage
      refetchData()
    }

    return {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      formatDate,
      activityTypeFilter,
      sectorIdsFilter,
      regionIdsFilter,
      remsFilter,
      unitNameFilter,
      facilityCategoryFilter,
      fechaInicioFilter,
      fechaFinFilter,
      statusFilter,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      goToPlanningCreate,
      updatePagination,
      goToPlanningDetail,
      resolveStatusLabel,
      getActorPrimary,
      getActorSecondary,
    }
  },
}
</script>
